/**
 * Chart configuration constants and factories
 * Centralizes chart styling and behavior for consistency
 */

export const CHART_COLORS = {
  primary: "#FFA500",
  primaryRgba: "rgba(255, 165, 0, 0.2)",
  secondary: "#26FBD7",
  secondaryRgba: "rgba(38, 251, 215, 0.2)",
  white: "#fff",
  gridColor: "rgba(255,255,255,0.1)",
};

export const CHART_FONTS = {
  family: "'Inter', sans-serif",
  sizeLarge: 14,
  sizeMedium: 13,
  sizeSmall: 12,
  sizeExtraSmall: 11,
};

export const CHART_ANIMATIONS = {
  duration: 750,
  easing: "easeInOutQuart",
  hoverDuration: 200,
};

/**
 * Creates chart options configuration for history performance chart
 * @param {Function} t - Translation function
 * @param {Array} sessions - Sessions data for tooltip context
 * @param {Function} findDartById - Function to find dart by ID
 * @returns {Object} Chart.js options object
 */
export const createChartOptions = (t, sessions, findDartById) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  plugins: {
    legend: {
      position: "top",
      labels: {
        color: CHART_COLORS.white,
        font: {
          family: CHART_FONTS.family,
          size: CHART_FONTS.sizeSmall,
        },
        padding: 15,
        usePointStyle: true,
        pointStyle: "circle",
      },
      onClick: function (e, legendItem, legend) {
        const index = legendItem.datasetIndex;
        const ci = legend.chart;

        // Toggle visibility of the clicked dataset
        if (ci.isDatasetVisible(index)) {
          ci.hide(index);
          legendItem.hidden = true;
        } else {
          ci.show(index);
          legendItem.hidden = false;
        }
      },
    },
    tooltip: {
      backgroundColor: "rgba(0, 0, 0, 0.85)",
      titleColor: CHART_COLORS.white,
      bodyColor: CHART_COLORS.white,
      padding: 12,
      cornerRadius: 8,
      titleFont: {
        family: CHART_FONTS.family,
        size: CHART_FONTS.sizeLarge,
        weight: "bold",
      },
      bodyFont: {
        family: CHART_FONTS.family,
        size: CHART_FONTS.sizeMedium,
      },
      displayColors: true,
      boxPadding: 6,
      boxWidth: 8,
      boxHeight: 8,
      callbacks: {
        label: function (context) {
          const index = context.dataIndex;
          const session = sessions[sessions.length - 1 - index];
          const date = session.createdAt
            ? session.createdAt.toDate().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              })
            : "???";
          const dart = findDartById(session.dartsUsed);
          const dartCode = dart ? dart.shortCode : "---";

          return [
            `${t("history.tooltip.date")}: ${date}`,
            `${t("history.tooltip.dart")}: ${dartCode}`,
            `${t("history.tooltip.501Average")}: ${
              context.dataset.label === t("history.chart.501Average")
                ? context.parsed.y.toFixed(1)
                : session.statistics?.avg_median?.toFixed(1) || "N/A"
            }`,
            `${t("history.tooltip.score")}: ${
              context.dataset.label === t("history.chart.score")
                ? context.parsed.y.toFixed(1)
                : session.statistics?.general_score?.toFixed(1) || "N/A"
            }`,
          ];
        },
      },
    },
  },
  scales: {
    x: {
      ticks: {
        color: CHART_COLORS.white,
        font: {
          family: CHART_FONTS.family,
          size: CHART_FONTS.sizeExtraSmall,
        },
        maxRotation: 45,
        minRotation: 45,
      },
      grid: {
        display: false,
        drawBorder: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      ticks: {
        color: CHART_COLORS.white,
        font: {
          family: CHART_FONTS.family,
          size: CHART_FONTS.sizeExtraSmall,
        },
        padding: 8,
      },
      grid: {
        color: CHART_COLORS.gridColor,
        drawBorder: false,
      },
      border: {
        display: false,
      },
      beginAtZero: true,
    },
  },
  elements: {
    line: {
      tension: 0.4,
      borderWidth: 2.5,
    },
    point: {
      radius: 4,
      hoverRadius: 6,
      borderWidth: 2,
      hoverBorderWidth: 3,
    },
  },
  hover: {
    mode: "index",
    intersect: false,
    animationDuration: CHART_ANIMATIONS.hoverDuration,
  },
  animation: {
    duration: CHART_ANIMATIONS.duration,
    easing: CHART_ANIMATIONS.easing,
  },
  onHover: (event, elements) => {
    const chart = event.chart;

    if (elements.length === 0) {
      chart.data.datasets.forEach((dataset) => {
        dataset.borderWidth = 2.5;
        dataset.pointRadius = 4;
      });
      chart.update("none");
      return;
    }

    const hoveredDatasetIndex = elements[0].datasetIndex;

    chart.data.datasets.forEach((dataset, i) => {
      if (i === hoveredDatasetIndex) {
        dataset.borderWidth = 4;
        dataset.pointRadius = 6;
      } else {
        dataset.borderWidth = 1.5;
        dataset.pointRadius = 3;
      }
    });

    chart.update("none");
  },
});

/**
 * Creates chart data configuration for history performance chart
 * @param {Array} sessions - Sessions data
 * @param {Function} t - Translation function
 * @returns {Object} Chart.js data object
 */
export const createChartData = (sessions, t) => ({
  labels: sessions
    .map((session) =>
      session.createdAt
        ? session.createdAt.toDate().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
          })
        : "???"
    )
    .reverse(),
  datasets: [
    {
      label: t("history.chart.501Average"),
      data: sessions
        .map((session) => session.statistics?.avg_median || 0)
        .reverse(),
      borderColor: CHART_COLORS.primary,
      backgroundColor: CHART_COLORS.primaryRgba,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: CHART_COLORS.primary,
      pointBorderColor: CHART_COLORS.white,
      pointBorderWidth: 2,
      pointHoverBackgroundColor: CHART_COLORS.primary,
      pointHoverBorderColor: CHART_COLORS.white,
      pointHoverBorderWidth: 3,
      fill: true,
      borderWidth: 2.5,
      shadowColor: "rgba(255, 165, 0, 0.5)",
      shadowBlur: 10,
      shadowOffsetX: 0,
      shadowOffsetY: 4,
    },
    {
      label: t("history.chart.score"),
      data: sessions
        .map((session) => session.statistics?.general_score || 0)
        .reverse(),
      borderColor: CHART_COLORS.secondary,
      backgroundColor: CHART_COLORS.secondaryRgba,
      tension: 0.4,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: CHART_COLORS.secondary,
      pointBorderColor: CHART_COLORS.white,
      pointBorderWidth: 2,
      pointHoverBackgroundColor: CHART_COLORS.secondary,
      pointHoverBorderColor: CHART_COLORS.white,
      pointHoverBorderWidth: 3,
      fill: true,
      borderWidth: 2.5,
      shadowColor: "rgba(38, 251, 215, 0.5)",
      shadowBlur: 10,
      shadowOffsetX: 0,
      shadowOffsetY: 4,
    },
  ],
});
